import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { api } from "helpers/api_url";
import moment from "moment";

export const fetchOrder = createAsyncThunk(
  "master/fetchOrder",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if (search) params.append("search", search);
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    if (filters?.length) {
      filters.forEach(
        ({
          filterField,
          filterValue,
        }: {
          filterField: string;
          filterValue: any;
        }) => {
          if (!filterField || filterValue === undefined || filterValue === null)
            return;

          // 🗓 Handle date-range filters (object with from_date / to_date)
          if (typeof filterValue === "object" && filterValue !== null) {
            const { from_date, to_date } = filterValue;

            if (
              from_date &&
              (filterField === "created_at" || filterField === "updated_at" || filterField === "order_date")
            ) {
              params.append(`${filterField}_from`, from_date);
            }

            if (
              to_date &&
              (filterField === "created_at" || filterField === "updated_at" || filterField === "order_date")
            ) {
              params.append(`${filterField}_to`, to_date);
            }
          }

          // 🔤 Handle simple (non-object) filters
          else {
            params.append(filterField, String(filterValue));
          }
        }
      );
    }

    const response: any = await Axios.get(
      `${api?.order?.list}?${params.toString()}`
    );

    return {
      data: response?.data?.data || [],
      rowCount: response.data.count || 0,
    };
  }
);

export const exportOrderCSV = async ({
  sort,
  order,
  search,
  filters,
}: {
  sort: any;
  order: any;
  search: any;
  filters: any;
}) => {
  const params = new URLSearchParams();
  if (search) params.append("search", search);
  if (sort) {
    const orderingValue = order === "desc" ? `-${sort}` : sort;
    params.append("ordering", orderingValue);
  }
  if (filters?.length) {
      filters.forEach(
        ({
          filterField,
          filterValue,
        }: {
          filterField: string;
          filterValue: any;
        }) => {
          if (!filterField || filterValue === undefined || filterValue === null)
            return;

          // 🗓 Handle date-range filters (object with from_date / to_date)
          if (typeof filterValue === "object" && filterValue !== null) {
            const { from_date, to_date } = filterValue;

            if (
              from_date &&
              (filterField === "created_at" || filterField === "updated_at" || filterField === "order_date")
            ) {
              params.append(`${filterField}_from`, from_date);
            }

            if (
              to_date &&
              (filterField === "created_at" || filterField === "updated_at" || filterField === "order_date")
            ) {
              params.append(`${filterField}_to`, to_date);
            }
          }

          // 🔤 Handle simple (non-object) filters
          else {
            params.append(filterField, String(filterValue));
          }
        }
      );
    }
  try {
    const response = await Axios.get(`${api?.order?.export}?${params.toString()}`, {
      params: { export: "csv" },
      responseType: "blob", // important
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `order_${moment().format("YYYY-MM-DD")}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error("Error downloading CSV", error);
  }
};

// Download order PDF
export const downloadOrderPdf = createAsyncThunk(
  "order/downloadOrderPdf",
  async (
    {
      id,
      orderNumber,
    }: {
      id: string;
      orderNumber?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await Axios.get(`/order/${id}/pdf`, {
        responseType: "blob",
      });

      const disposition =
        response.headers?.["content-disposition"] ||
        response.headers?.["Content-Disposition"];
      const matches = disposition?.match(/filename="?([^"]+)"?/i);
      const fileName = matches?.[1] || `order-${orderNumber || id}.pdf`;

      return { blob: response.data as Blob, filename: fileName };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data ||
          error?.message ||
          "Failed to download order PDF"
      );
    }
  }
);